import React, { useEffect, useState } from 'react';
import {
    makeStyles,
    mergeClasses,
    tokens,
    Button,
    Menu,
    MenuItem,
    MenuItemProps,
    MenuList,
    MenuPopover,
    MenuTrigger,
    Tab,
    TabList,
    useIsOverflowItemVisible,
    useOverflowMenu,
    Overflow,
    OverflowItem,
    Title1,
    MessageBar,
    MessageBarBody,
    MessageBarActions,
    Dialog,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
} from "@fluentui/react-components";
import {
    MoreHorizontalRegular,
    MoreHorizontalFilled,
    bundleIcon,
    Dismiss24Regular,
    DismissRegular,
} from "@fluentui/react-icons";
import deleteIconDarkMode from './Assets/fluentdeletewhite48.png';
import deleteIconLightMode from './Assets/fluentdeleteblack48.png';


import { appTheme } from './Helpers/customRevealTheme';
import { darkPowerONTheme, brandPowerON } from './Helpers/fluentPowerONTheme';
import { useSignInState } from './Components/signInState';
import { isBasecampWorkspaceAdminTier } from './constants/basecampWorkspaceRoleIds';
import { useAuth } from './context/AuthContext';
import { attachRevealSessionRevalidation } from './Helpers/attachRevealSessionRevalidation';
import useRevealSessionCheck from './Hooks/useRevealSessionCheck';
import LoadingPage from './Components/LoadingPage';
import DashboardLoader from './Components/DashboardLoader';
import useFetch from './Hooks/useFetchAPI';
import useRevealHeaders from './Hooks/useRevealHeaders';
import useAlerts from './Hooks/useAlerts';
import AlertModal from './Components/AlertModal';
import { Alert } from './Types/alert';
import { addAlertMenuItemToVisualization } from './Helpers/utils';
import {
  dotnetBackendRevealBaseUrl,
  dotnetBackendRevealCustomdashboardsUrl,
  dotnetBackendRevealDeletedashboardUrl,
} from './config/dotnetBackendEnv';

declare let $: any;

const MoreHorizontal = bundleIcon(MoreHorizontalFilled, MoreHorizontalRegular);

type OverflowMenuItemProps = {
    tab: DashboardDataResult;
    onClick: MenuItemProps["onClick"];
};

type OverflowMenuProps = {
    onTabSelect?: (tabname: string) => void;
};

const useStyles = makeStyles({
    menu: {
        backgroundColor: tokens.colorNeutralBackground1,
    },
    menuButton: {
        alignSelf: "center",
    },
    horizontal: {
        height: "fit-content",
        minWidth: "150px",
        resize: "horizontal",
        width: "600px",
    },
});

const CustomDashboards = (props: any) => {
    $.ig.RevealSdkSettings.setBaseUrl(dotnetBackendRevealBaseUrl);
    $.ig.RevealSdkSettings.enableActionsOnHoverTooltip = true;

    const styles = useStyles();

    const { signInState } = useSignInState();
    const [dashboardId, setDashboardId] = useState<string | null>(null);
    const [selectedTabId, setSelectedTabId] = React.useState<string>("");
    const [dashboards, setDashboards] = useState<DashboardDataResult[]>([]);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [dashboardSelectorCallback, setDashboardSelectorCallback] = useState<Function | null>(null);
    const { headers, getRevealHeaders } = useRevealHeaders();
    const { currentAlert, setCurrentAlert, createAlert, createAlertData } = useAlerts();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState('');
    const [isDashboardLoading, setIsDashboardLoading] = useState(false);
    const { refreshSession } = useAuth();
    useRevealSessionCheck(isDialogOpen || isModalOpen);

    const { fetchAPI: fetchDashboards, apiData: dashboardData, isLoading: isLoadingDashboards, serverError: serverErrorDashboards } = useFetch({ scope: 'AppUser' })
    const { fetchAPI: checkDashboards, apiData: checkDashboardData, isLoading: isCheckingDashboards, serverError: serverErrorCheckDashboards } = useFetch({ scope: 'AppUser' })
    const { fetchAPI: deleteDashboards, apiData: deleteDashboardData, isLoading: isDeletingDashboards, serverError: serverErrorDeleteDashboards } = useFetch({ scope: 'AdminUser' })

    const fetchDashList = async () => {
        await fetchDashboards({ url: dotnetBackendRevealCustomdashboardsUrl, method: 'GET' });
    };

    const checkDashList = async () => {
        await checkDashboards({ url: dotnetBackendRevealCustomdashboardsUrl, method: 'GET' });
    };

    const deleteDashboard = async (dashboardId: string) => {
        await deleteDashboards({ url: dotnetBackendRevealDeletedashboardUrl(dashboardId), method: 'DELETE' });
    };

    const revealHeaders = async () => {
        await getRevealHeaders();
    };

    useEffect(() => {
        $.ig.RevealSdkSettings.setAdditionalHeadersProvider(() => headers);
    }, [headers]);

    useEffect(() => {
        $.ig.RevealSdkSettings.theme = appTheme(signInState.userInfo?.ThemePreference);
        if (signInState.userInfo?.ThemePreference === 'light') {
            $.ig.RevealSdkSettings.theme.isDark = false;
        }
        else {
            $.ig.RevealSdkSettings.theme.isDark = true;
        }
        darkPowerONTheme.colorBrandForeground1 = brandPowerON[110]; // use brand[110] instead of brand[100]
        darkPowerONTheme.colorBrandForeground2 = brandPowerON[120]; // use brand[120] instead of brand[110]
    }, [signInState.userInfo?.ThemePreference]);

    useEffect(() => {
        if (!signInState.isLoading && signInState.isAuthed) {
            void revealHeaders();
        }
    }, [signInState.isLoading, signInState.isAuthed, props.currentCustomerID]);

    useEffect(() => {
        setDashboards([])
        setDashboardId(null)
        fetchDashList()
            .catch((error) => {
                console.error('Error fetching dashboards', error);
                setDashboards([]);
            });
    }, [props.currentCustomerID, isDeletingDashboards]);

    const updateRevealView = (revealView: any) => {
        revealView.onDashboardSelectorRequested = (args: any) => {
            setDashboardSelectorCallback(() => args.callback);
            setIsDialogOpen(true)
        }

        revealView.onLinkedDashboardProviderAsync = (dashboardId: string) => {
            return $.ig.RVDashboard.loadDashboard(dashboardId);
        };

        revealView.onUrlLinkRequested = function (args: any) {
            if (args?.url?.includes('DashboardLink')) {
                const hasAValue = args.row.find((row: any) => {
                    return row.__value !== null && row.__columnName === 'DashboardLink';
                });

                if (!hasAValue) {
                    console.warn('No DashboardLink value found in current row:', args.row);
                    return '';
                }
            }
            return args.url;
        };

        revealView.onSave = async (rv: any, args: any) => {
            console.log('RV Args:', args);
            if (args.saveAs) {
                const newName = prompt('Enter a new name for the dashboard:', args.name);
                console.log('Save As:', args.saveAs);
                args.dashboardId = args.name = newName;

                // if (args.name.contains(' ')) {
                //     //Need to find a better way to handle spaces in the name rather than stripping them out
                //     console.log('Name contains spaces:', args.name);
                //     args.dashboardId = args.name.replace(/\s/g, '');
                // }

                await checkDashList();
                const foundDasboardWithSameName = Array.isArray(checkDashboardData) && checkDashboardData.some((dashboard: any) => dashboard.name === args.dashboardId);
                if (foundDasboardWithSameName) {
                    alert('A dashboard with that name already exists. Please choose a different name.');
                    args.cancel = true;
                } else {
                    args.saveFinished();
                }
            }
            else if (args.name === 'New Dashboard') {
                alert('You must name the dashboard other then "New Dashboard" before saving.');
                args.cancel = true;
            }
            else {
                args.saveFinished();
            }
        }

        addAlertMenuItemToVisualization(revealView, signInState, setCurrentAlert);
        attachRevealSessionRevalidation(revealView, refreshSession);
    }

    useEffect(() => {
        if (!isDeletingDashboards && !isLoadingDashboards && !serverErrorDashboards && dashboardData && dashboardId) {
            console.log('CustomDashboards: load dashboard', dashboardId);

            // Set loading state when starting to load dashboard
            setIsDashboardLoading(true);

            $.ig.RVDashboard.loadDashboard(dashboardId).then((dashboard: any) => {
                var revealView = new $.ig.RevealView('#revealView');

                revealView.onDataSourcesRequested = (callback: any) => {
                    revealHeaders();

                    // Intune Full Database
                    let intuneDataSource = new $.ig.RVAzureSqlDataSource();
                    intuneDataSource.id = 'EntraIntune';
                    intuneDataSource.title = 'Entra and Intune Data Source';
                    intuneDataSource.subtitle = 'Entra and Intune Data';

                    // Intune Summary Tables Only
                    let intuneSummariesDataSource = new $.ig.RVAzureSqlDataSource();
                    intuneSummariesDataSource.id = 'IntuneSummaries';
                    intuneSummariesDataSource.title = 'Intune Summary Tables Data Source';
                    intuneSummariesDataSource.subtitle = 'Intune Data';

                    // Intune Summary Tables Only
                    let commonDataSource = new $.ig.RVAzureSqlDataSource();
                    commonDataSource.id = 'CommonData';
                    commonDataSource.title = 'Common Data Source';
                    commonDataSource.subtitle = 'Common Data';

                    // Intune Curated Items
                    let dataSourceItem1 = new $.ig.RVAzureSqlDataSourceItem(intuneDataSource);
                    dataSourceItem1.id = 'DeviceInfo';
                    dataSourceItem1.title = 'Common Device Information';
                    dataSourceItem1.subtitle = 'Information about devices in Entra and Intune';

                    let dataSourceItem2 = new $.ig.RVAzureSqlDataSourceItem(intuneDataSource);
                    dataSourceItem2.id = 'WindowsReleaseInfo';
                    dataSourceItem2.title = 'Windows Update Information';
                    dataSourceItem2.subtitle = 'Information about Windows Update state for devices';

                    callback(new $.ig.RevealDataSources([intuneDataSource, commonDataSource, intuneSummariesDataSource], [dataSourceItem1, dataSourceItem2], false));
                };

                const bypassWorkspaceGuards = signInState.isWorkspaceSuperAdmin;
                const canAuthorCustomDashboards = isBasecampWorkspaceAdminTier(
                  bypassWorkspaceGuards,
                  signInState.workspaceRoleId,
                );

                revealView.dashboard = dashboard;
                if (canAuthorCustomDashboards) {
                        
                    revealView.canEdit = true;
                    revealView.canSaveAs = true;

                    revealView.onMenuOpening = function (visualization: any, args: any) {

                        if (args.menuLocation === $.ig.RVMenuLocation.Dashboard) {
                            //adding a new menu item

                            console.log('Theme is Darkmode?:', $.ig.RevealSdkSettings.theme.isDark)
                            if ($.ig.RevealSdkSettings.theme.isDark) {
                                const menuItem = new $.ig.RVMenuItem("Delete Dashboard", new $.ig.RVImage(deleteIconDarkMode, "Icon"), () => {
                                    deleteDashboard(dashboardId);
                                })
                                args.menuItems.push(menuItem);
                            }
                            else {
                                const menuItem = new $.ig.RVMenuItem("Delete Dashboard", new $.ig.RVImage(deleteIconLightMode, "Icon"), () => {
                                    deleteDashboard(dashboardId);
                                })
                                args.menuItems.push(menuItem);
                            }
                        }

                    };

                }
                else {
                    revealView.canEdit = false;
                    revealView.canSaveAs = false;
                }

                updateRevealView(revealView);

                // Clear loading state
                setIsDashboardLoading(false);
            })
                .catch((error: any) => {
                    console.error('Error loading dashboard', error);
                    setDashboardId(null);

                    // Clear loading state
                    setIsDashboardLoading(false);
                });
        }
    }, [dashboardData, serverErrorDashboards, isLoadingDashboards, dashboardId, isDeletingDashboards]);

    useEffect(() => {
        console.log('CustomDashboards: dashboards', dashboardData);
        setDashboards(dashboardData as unknown as DashboardDataResult[]);
    }, [dashboardData]);

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setCurrentAlert(null);
    };

    useEffect(() => {
        if (createAlertData) {
            setNotificationMessage('Alert created successfully');
        }
    }, [createAlertData]);

    const handleSaveAlert = async (updatedAlert: any) => {
        try {
            await createAlert(updatedAlert);
            handleCloseModal();
        } catch (error) {
            console.error('Error saving alert:', error);
        }
    };
    useEffect(() => {
        if (Array.isArray(dashboards) && dashboards.length > 0 && !selectedTabId) {
            const firstDashboard = dashboards[0].name;
            setSelectedTabId(firstDashboard);
            setDashboardId(firstDashboard);
        }
    }, [dashboards, selectedTabId]);

    const onTabSelect = (tabname: string) => {
        setSelectedTabId(tabname);
        setDashboardId(tabname || null);
    };

    const OverflowMenuItem = (props: OverflowMenuItemProps) => {
        const { tab, onClick } = props;
        const isVisible = useIsOverflowItemVisible(tab.name);

        if (isVisible) {
            return null;
        }

        return (
            <MenuItem key={tab.name} onClick={onClick}>
                <div>{tab.name}</div>
            </MenuItem>
        );
    };

    const OverflowMenu = (props: OverflowMenuProps) => {
        const { onTabSelect } = props;
        const { ref, isOverflowing, overflowCount } =
            useOverflowMenu<HTMLButtonElement>();

        const onItemClick = (tabname: string) => {
            onTabSelect?.(tabname);
        };

        if (!isOverflowing) {
            return null;
        }

        return (
            <Menu hasIcons>
                <MenuTrigger disableButtonEnhancement>
                    <Button
                        appearance="transparent"
                        className={styles.menuButton}
                        ref={ref}
                        icon={<MoreHorizontal />}
                        aria-label={`${overflowCount} more tabs`}
                        role="tab"
                    />
                </MenuTrigger>
                <MenuPopover>
                    <MenuList className={styles.menu}>
                        {dashboards.map((tab) => (
                            <OverflowMenuItem
                                key={tab.name}
                                tab={tab}
                                onClick={() => onItemClick(tab.name)}
                            />
                        ))}
                    </MenuList>
                </MenuPopover>
            </Menu>
        );
    };

    return (
        <div className='Dashboard'>
            {notificationMessage && (
                <MessageBar intent={'success'}>
                    <MessageBarBody>
                        {notificationMessage}
                    </MessageBarBody>
                    <MessageBarActions
                        containerAction={
                            <Button
                                onClick={() => setNotificationMessage('')}
                                aria-label="dismiss"
                                appearance="transparent"
                                icon={<DismissRegular />}
                            />
                        }
                    />
                </MessageBar>
            )}
            {(isLoadingDashboards || isDeletingDashboards) && <LoadingPage message="Loading Dashboards..." />}
            {!isLoadingDashboards &&
                <>
                    <Dialog modalType="alert"
                        open={isDialogOpen}
                        onOpenChange={(event, data) => {
                            setIsDialogOpen(data.open);
                        }}>
                        <DialogSurface>
                            <DialogBody>
                                <DialogTitle
                                    action={
                                        <DialogTrigger action="close">
                                            <Button
                                                appearance="subtle"
                                                aria-label="close"
                                                icon={<Dismiss24Regular />}
                                            />
                                        </DialogTrigger>
                                    }
                                >
                                    Choose a dashboard to link
                                </DialogTitle>
                                <DialogContent>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between' }}>
                                        {Array.isArray(dashboardData) ? dashboardData.map((item: { name: string }) => (
                                            <div style={{ flex: '1 0 170px', maxWidth: '170px', margin: '10px' }}>
                                                <Button key={item.name} onClick={() => {
                                                    setIsDialogOpen(false);
                                                    if (dashboardSelectorCallback) {
                                                        dashboardSelectorCallback(item.name);
                                                    }
                                                }} style={{
                                                    maxWidth: '170px',
                                                    minWidth: '170px',
                                                    height: '3em',
                                                }}>
                                                    {item.name}
                                                </Button>
                                            </div>
                                        )) : null}
                                    </div>
                                </DialogContent>
                            </DialogBody>
                        </DialogSurface>
                    </Dialog>

                    {Array.isArray(dashboards) && dashboards.length > 0 ? (
                        <div className={mergeClasses(styles.horizontal)} >
                            <Overflow minimumVisible={2}>
                                <TabList
                                    selectedValue={selectedTabId}
                                    onTabSelect={(_, d) => onTabSelect(d.value as string)}
                                >
                                    {dashboards.map((tab) => {
                                        return (
                                            <OverflowItem
                                                key={tab.name}
                                                id={tab.name}
                                                priority={tab.name === selectedTabId ? 2 : 1}
                                            >
                                                <Tab value={tab.name}  >
                                                    {tab.name}
                                                </Tab>
                                            </OverflowItem>
                                        );
                                    })}
                                    <OverflowMenu onTabSelect={onTabSelect} />
                                </TabList>
                            </Overflow>
                        </div>
                    ) : (
                        <Title1>Create a new custom dashboard to get started here</Title1>
                    )}

                    <div hidden={isDashboardLoading} id="revealView" style={{ height: "100vh", width: "100%" }}></div>

                    <DashboardLoader isLoading={isDashboardLoading} loadingText="Loading dashboard data ..." />
                </>
            }
            {currentAlert && (
                <AlertModal
                    alert={currentAlert as Alert}
                    isOpen={isModalOpen}
                    onClose={handleCloseModal}
                    onSave={handleSaveAlert}
                    title={`Create Alert: ${currentAlert.AlertTitle}`}
                />
            )}
        </div>
    );

};

export default CustomDashboards;